import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ShieldCheck, ArrowRight, MapPin, Phone, Mail, Users, Compass,
  Home as HomeIcon, Building2,
} from "lucide-react";
import { useSEO, breadcrumbSchema, faqPageSchema, locationHubSchema, SITE_URL } from "../lib/seo";
import { STATES, CITIES } from "../data/nationalArchitecture";
import { SERVICE_BY_SLUG } from "../data/services";
import { trackPhoneClick } from "../lib/tracking";

const CAPABILITY_LABEL = {
  hub: { label: "Active operational hub", tone: "green" },
  serviced: { label: "National service capability", tone: "muted" },
  capability: { label: "National service capability", tone: "muted" },
};

export default function StateHubPage() {
  const { state: stateSlug } = useParams();
  const s = STATES[stateSlug];

  // Attach related cities from CITIES that share this state.
  const relatedCities = s ? Object.values(CITIES).filter((c) => c.stateSlug === s.slug) : [];

  useSEO({
    title: s
      ? `NDIS Provider ${s.name} | Alpha Community Care ${s.code}`
      : "NDIS Provider Australia | Alpha Community Care",
    description: s
      ? `${s.intro} ${s.positioning}`.slice(0, 240)
      : "Alpha Community Care — a registered NDIS provider servicing participants across Australia.",
    keywords: s ? [
      `NDIS provider ${s.name}`,
      `disability support ${s.name}`,
      `NDIS registered ${s.code}`,
      `SIL provider ${s.name}`,
      `support coordination ${s.name}`,
    ] : [],
    canonical: s ? `${SITE_URL}/locations/${s.slug}` : `${SITE_URL}/locations`,
    jsonLd: s ? [
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Locations", path: "/locations" },
        { name: s.name, path: `/locations/${s.slug}` },
      ]),
      faqPageSchema(s.faqs),
      ...(s.capability === "hub" && CITIES[s.hubCity] ? [
        locationHubSchema({
          name: CITIES[s.hubCity].name,
          state: s.code,
          region: s.name,
          suburb: CITIES[s.hubCity].suburb,
          postalCode: CITIES[s.hubCity].postalCode,
          latitude: CITIES[s.hubCity].lat,
          longitude: CITIES[s.hubCity].lon,
          slug: `locations/${s.slug}/${s.hubCity}`,
        }),
      ] : []),
    ] : [],
  });

  if (!s) return <Navigate to="/locations" replace />;

  const cap = CAPABILITY_LABEL[s.capability];
  const isHub = s.capability === "hub";

  return (
    <div data-testid={`page-state-${s.slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden alpha-gradient-hero alpha-noise border-b border-[color:var(--alpha-line)]">
        <div className="alpha-orb alpha-orb-purple w-[320px] h-[320px] -left-16 top-10 opacity-70 hidden md:block" aria-hidden />
        <div className="alpha-container pt-10 pb-12 lg:pt-16 lg:pb-16 relative">
          <div className="flex items-center gap-2 text-sm text-[color:var(--alpha-mute)]">
            <Link to="/locations" className="hover:text-[color:var(--alpha-purple-700)]">Locations</Link>
            <span className="opacity-50">/</span>
            <span className="text-[color:var(--alpha-ink)] font-semibold">{s.name}</span>
          </div>

          <div className="mt-6 grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--alpha-line-strong)] bg-white text-xs font-semibold ${isHub ? "text-[color:var(--alpha-green-700)]" : "text-[color:var(--alpha-purple-700)]"}`}>
                <ShieldCheck className="w-3.5 h-3.5" /> {cap.label} · {s.code}
              </div>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[56px] font-display font-medium tracking-tight leading-[1.05]">
                NDIS provider in <span className="alpha-serif-italic alpha-gradient-text">{s.name}</span>.
              </h1>
              <p className="mt-4 text-lg text-[color:var(--alpha-mute)] max-w-2xl">{s.tagline}.</p>
            </div>
            <div className="lg:col-span-4">
              <div className="alpha-card p-6">
                <div className="alpha-overline">Talk to intake</div>
                <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", `state-${s.slug}`)} className="mt-2 font-display text-2xl font-semibold alpha-gradient-text inline-flex items-center gap-2">
                  <Phone className="w-5 h-5" /> 1300 380 692
                </a>
                <Link to="/referrals" className="alpha-btn-primary w-full justify-center mt-4" data-testid={`state-${s.slug}-refer`}>
                  Refer in {s.name} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro + positioning */}
      <section className="alpha-section">
        <div className="alpha-container max-w-4xl">
          <div className="alpha-overline">About Alpha in {s.name}</div>
          <p className="mt-4 text-lg text-[color:var(--alpha-ink)] leading-relaxed">{s.intro}</p>
          <p className="mt-4 text-lg text-[color:var(--alpha-mute)] leading-relaxed">{s.positioning}</p>
        </div>
      </section>

      {/* Services in this state */}
      <section className="alpha-section bg-[color:var(--alpha-bg-soft)]">
        <div className="alpha-container">
          <div className="alpha-overline">Services in {s.name}</div>
          <h2 className="mt-3 text-3xl sm:text-[40px] font-display font-medium tracking-tight leading-[1.05] max-w-2xl">
            What Alpha genuinely offers here.
          </h2>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {s.services.map((svc) => {
              const base = SERVICE_BY_SLUG[svc.slug];
              const Icon = base?.Icon || HomeIcon;
              return (
                <Link key={svc.slug} to={`/services/${svc.slug}`} className="alpha-card p-6 group hover:-translate-y-1 transition-transform" data-testid={`state-${s.slug}-svc-${svc.slug}`}>
                  <div className="w-11 h-11 rounded-xl bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center group-hover:bg-[color:var(--alpha-purple-700)] group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold">{svc.label}</h3>
                  <p className="mt-2 text-sm text-[color:var(--alpha-mute)]">{svc.note}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--alpha-purple-700)] group-hover:gap-2 transition-all">Learn more <ArrowRight className="w-3.5 h-3.5" /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cities in this state */}
      {relatedCities.length > 0 && (
        <section className="alpha-section">
          <div className="alpha-container">
            <div className="alpha-overline">Cities we cover in {s.name}</div>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-display font-medium tracking-tight leading-[1.05] max-w-2xl">
              Where Alpha delivers, and where we assess case-by-case.
            </h2>
            <div className="mt-8 grid md:grid-cols-2 gap-5">
              {relatedCities.map((c) => {
                const cCap = CAPABILITY_LABEL[c.capability];
                const cIsHub = c.capability === "hub";
                return (
                  <Link key={c.slug} to={`/locations/${s.slug}/${c.slug}`} className="alpha-card p-7 group" data-testid={`state-${s.slug}-city-${c.slug}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl ${cIsHub ? "alpha-gradient text-white" : "bg-[color:var(--alpha-purple-100)] text-[color:var(--alpha-purple-700)]"} flex items-center justify-center`}>
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-display text-xl font-semibold">{c.name}</div>
                        <div className={`text-[11px] font-semibold uppercase tracking-[0.15em] mt-0.5 ${cIsHub ? "text-[color:var(--alpha-green-700)]" : "text-[color:var(--alpha-mute)]"}`}>{cCap.label}</div>
                      </div>
                    </div>
                    <p className="mt-4 text-sm text-[color:var(--alpha-mute)] line-clamp-2">{c.tagline}.</p>
                    <div className="mt-4 text-sm font-semibold text-[color:var(--alpha-purple-700)] group-hover:underline">NDIS support in {c.name} <ArrowRight className="w-3.5 h-3.5 inline" /></div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Referral model */}
      <section className="alpha-section bg-[color:var(--alpha-bg-soft)]">
        <div className="alpha-container grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="alpha-overline">Referral process</div>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-display font-medium tracking-tight leading-[1.05]">Referring from {s.name}.</h2>
            <p className="mt-4 text-[color:var(--alpha-mute)]">{s.referral}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/referrals/participant" className="alpha-btn-primary" data-testid={`state-${s.slug}-refer-p`}>Participant referral <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/referrals/coordinator" className="alpha-btn-green" data-testid={`state-${s.slug}-refer-c`}>Coordinator referral <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="alpha-card p-7 border-[color:var(--alpha-line-strong)] bg-white">
              <div className="alpha-overline">Beyond {s.name} metro</div>
              <p className="mt-3 text-[color:var(--alpha-ink)] leading-relaxed">{s.outsideMetro}</p>
            </div>
          </div>
        </div>
      </section>

      {/* State FAQs */}
      <section className="alpha-section">
        <div className="alpha-container max-w-3xl">
          <div className="alpha-overline">{s.name} FAQs</div>
          <h2 className="mt-3 font-display text-3xl font-medium">Common {s.name} questions.</h2>
          <div className="mt-6 space-y-3">
            {s.faqs.map((f, i) => (
              <details key={f.q} className="alpha-card p-0 overflow-hidden" data-testid={`state-${s.slug}-faq-${i}`}>
                <summary className="list-none p-5 flex items-center justify-between cursor-pointer">
                  <span className="font-display text-base font-semibold pr-4">{f.q}</span>
                  <span className="text-[color:var(--alpha-purple-600)] font-bold">+</span>
                </summary>
                <div className="px-5 pb-5 text-sm text-[color:var(--alpha-mute)] leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="alpha-section">
        <div className="alpha-container">
          <div className="relative overflow-hidden rounded-[28px] alpha-gradient p-10 sm:p-14 text-white">
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <h2 className="font-display text-3xl sm:text-4xl font-medium leading-tight !text-white">
                  Talk to intake about {s.name}.
                </h2>
                <p className="mt-3 text-white/85 max-w-2xl">{isHub ? "Named coordinators and active hub delivery." : "National intake with honest capacity feedback."}</p>
              </div>
              <div className="md:col-span-4 flex flex-col gap-3">
                <a href="tel:1300380692" className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-4 font-semibold text-center inline-flex items-center justify-center gap-2"><Phone className="w-4 h-4" /> 1300 380 692</a>
                <Link to="/vacancies" className="border border-white/60 rounded-full px-6 py-4 font-semibold text-center hover:bg-white/10 transition inline-flex items-center justify-center gap-2"><HomeIcon className="w-4 h-4" /> Find a home</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
